import { useState } from "react";
import { C, FONT } from "../theme";
import { useToast } from "./Toast";
import type { ApiKey } from "../lib/api";

const sm: React.CSSProperties = {
  background: "transparent",
  border: `1px solid ${C.line3}`,
  color: "#9fb6a8",
  padding: "7px 10px",
  borderRadius: 5,
  font: `600 11px ${FONT}`,
  cursor: "pointer",
};

export function KeyRow({
  k,
  plaintext,
  onReveal,
  onRotate,
  onDelete,
  onAddDomain,
  onRemoveDomain,
}: {
  k: ApiKey;
  // Plaintext is only known right after create/rotate (server stores a hash).
  plaintext?: string;
  onReveal: () => void; // parent decides whether plaintext is available
  onRotate: () => void;
  onDelete: () => void;
  onAddDomain: (domain: string) => void;
  onRemoveDomain: (domain: string) => void;
}) {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [draft, setDraft] = useState("");
  const toast = useToast();

  const shown = revealed && plaintext ? plaintext : k.display;

  const reveal = () => {
    if (!plaintext) {
      onReveal();
      toast("Keys are shown once at creation. Rotate to issue a new secret.");
      return;
    }
    setRevealed((r) => !r);
  };

  const copy = () => {
    const v = plaintext ?? k.display;
    navigator.clipboard?.writeText(v).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const addDraft = () => {
    const d = draft.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");
    if (d) onAddDomain(d);
    setDraft("");
  };

  return (
    <div style={{ padding: "15px 0", borderTop: `1px solid ${C.line}` }}>
      <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 12 }}>
        <div style={{ flex: "none", width: 120 }}>
          <div style={{ color: "#d9ffe6", fontSize: 13, fontWeight: 600 }}>{k.name}</div>
          <div style={{ color: C.mutedDim, fontSize: 11, marginTop: 3 }}>created {k.created}</div>
        </div>
        <code style={{ flex: 1, minWidth: 0, background: C.inset, border: `1px solid ${C.line}`, borderRadius: 5, padding: "9px 11px", color: C.ac, fontSize: 12.5, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {shown}
        </code>
        <div style={{ display: "flex", gap: 6, flex: "none" }}>
          <button onClick={reveal} style={sm}>{revealed && plaintext ? "hide" : "reveal"}</button>
          <button onClick={copy} style={sm}>{copied ? "copied ✓" : "copy"}</button>
          <button onClick={onRotate} style={sm}>rotate</button>
          <button onClick={onDelete} style={{ ...sm, border: `1px solid ${C.dangerLine}`, color: "#d98a8a" }}>del</button>
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 11 }}>
        <span style={{ color: C.mutedDim, fontSize: 11, flex: "none" }}>allowed domains</span>
        {k.domains.length > 0 ? (
          k.domains.map((d) => (
            <span key={d} style={{ display: "inline-flex", alignItems: "center", gap: 7, background: "rgba(54,224,127,.08)", border: `1px solid ${C.line3}`, color: C.text, padding: "4px 6px 4px 10px", borderRadius: 5, fontSize: 11.5 }}>
              {d}
              <span onClick={() => onRemoveDomain(d)} style={{ color: C.muted, cursor: "pointer", fontSize: 13, lineHeight: 1 }}>×</span>
            </span>
          ))
        ) : (
          <span style={{ color: "#6e6a64", fontSize: 11, fontStyle: "italic" }}>any domain (unrestricted)</span>
        )}
        <input
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && addDraft()}
          placeholder="add domain…"
          style={{ flex: "none", width: 150, background: C.bg, border: `1px solid ${C.line2}`, borderRadius: 5, padding: "5px 9px", color: C.textBrightest, font: `500 11.5px ${FONT}` }}
        />
        <button onClick={addDraft} style={{ background: "transparent", border: `1px solid ${C.line3}`, color: C.ac, padding: "5px 11px", borderRadius: 5, font: `600 11px ${FONT}`, cursor: "pointer" }}>+ add</button>
      </div>
    </div>
  );
}
